import { Sensor, SensorReadingCriteria2, SensorReadingType, TimeZoneDisplay } from '@models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { SensorService } from '@services/sensor.service';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CommonShared } from './common';
import { SelectionDateRange } from './dateRangeCodec';
import { backfillGpsLocations, isGpsReadingType } from './gpsBackfill';

// What to load readings for.
export interface ReadingsLoadRequest {
  sensors: Sensor[];
  // The checked reading types.
  readingTypes: SensorReadingType[];
  // Every reading type the sensors have, used to also fetch their latitude / longitude
  // types (which place the pins even when the user did not check them). When omitted
  // they are looked up from the server.
  availableReadingTypes?: SensorReadingType[];
  dateRange: SelectionDateRange;
  timeZone: TimeZoneDisplay;
  timeZoneOffset: number;
}

export interface ReadingsLoadResult {
  // The readings of the checked reading types for the requested sensors, GPS resolved.
  readings: SensorReadingInfo[];
  // True when this call fetched something from the server (false = served from the cache).
  fetched: boolean;
  // Readings returned by the server in this call.
  fetchedCount: number;
}

// Loads the readings of a sensor / reading-type / date-range selection for a map.
//
// Readings are fetched per reading type and kept in a cache, so re-checking a type costs
// no call. Call clear() whenever the sensors, date range or time zone change (the cached
// readings belong to the previous ones). A newer load() supersedes an older one still in
// flight, whose Observable then emits null.
export class SelectionReadingsLoader {
  private cache = new Map<number, SensorReadingInfo[]>();
  private token = 0;
  private common = new CommonShared();

  constructor(private sensorService: SensorService) {}


  clear(): void {
    this.cache.clear();
  }


  // Ignore the result of any load still in flight.
  cancel(): void {
    this.token++;
  }


  load(request: ReadingsLoadRequest): Observable<ReadingsLoadResult | null> {
    if (request.sensors.length === 0 || request.readingTypes.length === 0) {
      this.token++;
      return of({ readings: [], fetched: false, fetchedCount: 0 });
    }

    const token = ++this.token;

    const available$: Observable<SensorReadingType[]> = (request.availableReadingTypes != undefined)
      ? of(request.availableReadingTypes)
      : this.sensorService.getReadingTypesForSensors(request.sensors.map(s => s.id), '').pipe(map(types => types ?? []));

    return available$.pipe(switchMap(available => {
      if (token !== this.token) {
        return of(null);   // superseded
      }

      const missingTypes = this.typesToLoad(request.readingTypes, available).filter(rt => !this.cache.has(rt.id));
      if (missingTypes.length === 0) {
        return of(this.result(request, false, 0));
      }

      const criteria: SensorReadingCriteria2 = {
        sensorIds: request.sensors.map(s => s.id),
        startDate: request.dateRange.startDate.format('YYYY-MM-DD HH:mm'),
        endDate: request.dateRange.endDate.format('YYYY-MM-DD HH:mm'),
        timeZoneDisplay: request.timeZone,
        timeZoneOffset: request.timeZoneOffset,
        // Only the types not loaded yet -- the API returns every type when this is empty.
        sensorReadingTypes: missingTypes
      };

      return this.sensorService.getSensorReadingsForSensor(criteria).pipe(map((results: SensorReadingInfo[]) => {
        if (token !== this.token) {
          return null;   // superseded
        }

        const sensorNameMap = new Map<number, string>();
        request.sensors.forEach(s => sensorNameMap.set(s.id, this.common.getSensorDisplayName(s)));

        const loaded: SensorReadingInfo[] = (results ?? []).filter(
          s => s.value != 'nan' && s.value != 'NaN' && s.origValue != 'nan' && s.origValue != 'NaN');
        loaded.forEach(reading => {
          reading.createdDate = new Date(reading.createdDate);
          reading.sensorName = sensorNameMap.get(reading.sensorID) || '';
          reading.value = Number(reading.value).toPrecision(9);
          reading.origValue = Number(reading.origValue).toPrecision(9);
        });

        // Seed every requested type so one with no readings is not requested again.
        missingTypes.forEach(rt => this.cache.set(rt.id, []));
        loaded.forEach(reading => {
          const cached = this.cache.get(reading.typeID);
          if (cached != undefined) {
            cached.push(reading);
          } else {
            this.cache.set(reading.typeID, [reading]);
          }
        });

        return this.result(request, true, loaded.length);
      }));
    }));
  }


  // The checked reading types plus the latitude / longitude types of the sensors.
  private typesToLoad(checked: SensorReadingType[], available: SensorReadingType[]): SensorReadingType[] {
    const needed = [ ...checked ];
    available.forEach(rt => {
      if (isGpsReadingType(rt.type) && !needed.some(x => x.id === rt.id)) {
        needed.push(rt);
      }
    });
    return needed;
  }


  // The cached readings of the checked types for the requested sensors, GPS resolved.
  private result(request: ReadingsLoadRequest, fetched: boolean, fetchedCount: number): ReadingsLoadResult {
    const all: SensorReadingInfo[] = [];
    this.cache.forEach(readings => readings.forEach(r => all.push(r)));

    backfillGpsLocations(all, request.sensors);

    const typeIds = new Set(request.readingTypes.map(t => t.id));
    const sensorIds = new Set(request.sensors.map(s => s.id));

    return {
      readings: all.filter(r => typeIds.has(r.typeID) && sensorIds.has(r.sensorID)),
      fetched,
      fetchedCount
    };
  }
}
